import { fetchHealth, fetchReadiness, type Probe } from '../api';
import { card, definitionList, el, statusPill, type StatusTone } from '../dom';
import { config, previewInfrastructure } from '../config';

function describeApi(probe: Probe<{ status: string; version: string; commit: string }>): {
  tone: StatusTone;
  label: string;
  body: string;
} {
  switch (probe.state) {
    case 'unconfigured':
      return {
        tone: 'pending',
        label: 'Not deployed',
        body: 'No API base URL is baked into this build. The Dubbed clinic API stack is defined in IaC but has not been deployed.',
      };
    case 'pending':
      return { tone: 'pending', label: 'Checking', body: 'Contacting the Dubbed clinic API.' };
    case 'ok':
      return {
        tone: 'live',
        label: 'Healthy',
        body: `The Dubbed clinic API answered its health probe. Version ${probe.data.version}, commit ${probe.data.commit}.`,
      };
    case 'error':
      return {
        tone: 'blocked',
        label: 'Unreachable',
        body: `The Dubbed clinic API did not answer: ${probe.message}`,
      };
  }
}

function describeDatabase(probe: Probe<{ checks: Record<string, { status: string; detail: string }> }>): {
  tone: StatusTone;
  label: string;
  body: string;
} {
  if (probe.state === 'ok') {
    const database = probe.data.checks['database'];
    if (database?.status === 'ok') {
      return { tone: 'live', label: 'Connected', body: database.detail };
    }
    if (database) {
      return { tone: 'pending', label: 'Not created', body: database.detail };
    }
  }

  return {
    tone: 'pending',
    label: 'Not created',
    body: 'Empty greenfield instance identifier "dubbed" is stated to exist (0-row spine). This API is not wired to it, and the spine is not a clinic chart.',
  };
}

export function renderStatus(mount: HTMLElement): void {
  const heading = el(
    'section',
    { class: 'section' },
    el('h2', {}, 'Lane status'),
    el(
      'p',
      { class: 'lede' },
      'Where the Dubbed clinic lane stands on AWS. This page reflects the greenfield build only — it says nothing about the live Dubbed product.',
    ),
  );

  const grid = el('div', { class: 'grid' });

  const deliveryCard = card({
    title: 'Preview delivery',
    tone: 'live',
    toneLabel: 'Live',
    body: 'Static assets are served over HTTPS from CloudFront, backed by a private S3 origin. No custom domain is attached.',
    facts: [
      ['Distribution', previewInfrastructure.distributionId],
      ['Origin bucket', previewInfrastructure.originBucket],
      ['Custom domain', 'none — by design'],
    ],
  });

  const apiPending = describeApi({ state: 'pending' });
  const apiCard = el(
    'div',
    { 'data-slot': 'api' },
    card({
      title: 'Clinic API',
      tone: apiPending.tone,
      toneLabel: apiPending.label,
      body: apiPending.body,
    }),
  );

  const dbPending = describeDatabase({ state: 'pending' });
  const dbCard = el(
    'div',
    { 'data-slot': 'database' },
    card({
      title: 'Clinic database',
      tone: dbPending.tone,
      toneLabel: dbPending.label,
      body: dbPending.body,
    }),
  );

  const holdsCard = card({
    title: 'Holds in force',
    tone: 'held',
    toneLabel: 'Held',
    body: 'Live Dubbed stays on its current platform until there is an explicit go. No migration, no data copy, no cutover, and no DNS change is part of this build.',
    facts: [
      ['Migration', 'not started, not planned here'],
      ['DNS', 'untouched'],
      ['Data', 'none — empty spine, 0 rows, no copy'],
      ['Staff door', 'MFA required; Google + password; paper Cognito'],
      ['Clinic OS', 'schema + API + admin slice; empty store'],
    ],
  });

  grid.append(deliveryCard, apiCard, dbCard, holdsCard);
  mount.append(heading, grid);

  void refreshProbes(apiCard, dbCard);
}

async function refreshProbes(apiSlot: HTMLElement, dbSlot: HTMLElement): Promise<void> {
  const [health, readiness] = await Promise.all([fetchHealth(), fetchReadiness()]);

  const api = describeApi(health);
  apiSlot.replaceChildren(
    card({
      title: 'Clinic API',
      tone: api.tone,
      toneLabel: api.label,
      body: api.body,
      facts: [
        ['Health endpoint', config.apiBaseUrl ? `${config.apiBaseUrl}/health` : 'not configured'],
        ['Chart routes', 'stubbed — 501; empty spine is not a chart'],
        ['Staff door', 'GET /v1/staff/me — human token only'],
      ],
    }),
  );

  const database = describeDatabase(readiness);
  dbSlot.replaceChildren(
    card({
      title: 'Clinic database',
      tone: database.tone,
      toneLabel: database.label,
      body: database.body,
      facts: [
        ['Engine', 'PostgreSQL 16'],
        ['Spine', 'clinics, locations, staff_users, patients, encounters, chart_notes'],
        ['Rows', '0 — stated, not read from this preview'],
      ],
    }),
  );
}

export function renderStatusHeaderBadge(): HTMLElement {
  return el(
    'div',
    { class: 'env' },
    statusPill('pending', 'Preview'),
    el('span', { class: 'env__meta' }, `${previewInfrastructure.region} · ${config.commit}`),
  );
}

export function renderBuildFacts(): HTMLElement {
  return definitionList([
    ['Environment', config.environment],
    ['Built', config.builtAt],
    ['Commit', config.commit],
  ]);
}
